import { AGENT_URL } from '@/config/env'
import { fetchJsonWithAuth, mutateJsonWithAuth } from '@/lib/api'

import type {
  CreateSnippetInput,
  CustomizeSnippetInput,
  PutSnippetVariantInput,
  Snippet,
  SnippetImpact,
  SnippetUsage,
  SnippetVariant,
  SnippetVariantsResponse,
  SnippetsResponse,
  UpdateSnippetInput,
} from './types'

const SNIPPETS_URL = `${AGENT_URL}/snippets`

/** `dutyId` additionally returns that duty's own wording per snippet. */
export function fetchSnippets(
  token: string,
  signal?: AbortSignal,
  dutyId?: string
): Promise<SnippetsResponse> {
  const query = dutyId ? `?duty_id=${encodeURIComponent(dutyId)}` : ''
  return fetchJsonWithAuth<SnippetsResponse>({
    token,
    url: `${SNIPPETS_URL}${query}`,
    signal,
    errorMessage: 'Failed to load snippets',
  })
}

export function createSnippet(
  token: string,
  input: CreateSnippetInput
): Promise<Snippet> {
  return mutateJsonWithAuth<Snippet>({
    token,
    url: SNIPPETS_URL,
    method: 'POST',
    body: input,
    errorMessage: 'Failed to create snippet',
  })
}

export function patchSnippet(
  token: string,
  id: string,
  input: UpdateSnippetInput
): Promise<Snippet> {
  return mutateJsonWithAuth<Snippet>({
    token,
    url: `${SNIPPETS_URL}/${encodeURIComponent(id)}`,
    method: 'PATCH',
    body: input,
    errorMessage: 'Failed to update snippet',
  })
}

/** One snippet. A shared Careforce snippet is readable from any org. */
export function fetchSnippet(
  token: string,
  id: string,
  signal?: AbortSignal
): Promise<Snippet> {
  return fetchJsonWithAuth<Snippet>({
    token,
    url: `${SNIPPETS_URL}/${encodeURIComponent(id)}`,
    signal,
    errorMessage: 'Failed to load snippet',
  })
}

export function customizeSnippet(
  token: string,
  id: string,
  input: CustomizeSnippetInput
): Promise<Snippet> {
  return mutateJsonWithAuth<Snippet>({
    token,
    url: `${SNIPPETS_URL}/${encodeURIComponent(id)}/customize`,
    method: 'POST',
    body: input,
    errorMessage: 'Failed to save your copy',
  })
}

export function fetchSnippetUsage(
  token: string,
  id: string,
  signal?: AbortSignal
): Promise<SnippetUsage> {
  return fetchJsonWithAuth<SnippetUsage>({
    token,
    url: `${SNIPPETS_URL}/${encodeURIComponent(id)}/usage`,
    signal,
    errorMessage: 'Failed to load usage',
  })
}

/** Answers with the duties whose prompts still hold the deleted id. */
export function deleteSnippet(
  token: string,
  id: string
): Promise<{ referencing_duty_ids: string[] }> {
  return mutateJsonWithAuth<{ referencing_duty_ids: string[] }>({
    token,
    url: `${SNIPPETS_URL}/${encodeURIComponent(id)}`,
    method: 'DELETE',
    errorMessage: 'Failed to delete snippet',
  })
}

export function checkSnippetImpact(
  token: string,
  id: string,
  content: string
): Promise<SnippetImpact> {
  return mutateJsonWithAuth<SnippetImpact>({
    token,
    url: `${SNIPPETS_URL}/${encodeURIComponent(id)}/impact`,
    method: 'POST',
    body: { content },
    errorMessage: 'Failed to check snippet impact',
  })
}

function variantUrl(snippetId: string, dutyId: string): string {
  return `${SNIPPETS_URL}/${encodeURIComponent(snippetId)}/variants/${encodeURIComponent(dutyId)}`
}

export function fetchSnippetVariants(
  token: string,
  snippetId: string,
  signal?: AbortSignal
): Promise<SnippetVariantsResponse> {
  return fetchJsonWithAuth<SnippetVariantsResponse>({
    token,
    url: `${SNIPPETS_URL}/${encodeURIComponent(snippetId)}/variants`,
    signal,
    errorMessage: 'Failed to load variants',
  })
}

export function putSnippetVariant(
  token: string,
  snippetId: string,
  dutyId: string,
  input: PutSnippetVariantInput
): Promise<SnippetVariant> {
  return mutateJsonWithAuth<SnippetVariant>({
    token,
    url: variantUrl(snippetId, dutyId),
    method: 'PUT',
    body: input,
    errorMessage: 'Failed to save the variant',
  })
}

export function deleteSnippetVariant(
  token: string,
  snippetId: string,
  dutyId: string
): Promise<void> {
  return mutateJsonWithAuth<void>({
    token,
    url: variantUrl(snippetId, dutyId),
    method: 'DELETE',
    errorMessage: 'Failed to reset this duty to the primary snippet',
  })
}
